import { useEffect, useId, useRef, type ReactNode } from "react";
import { useT } from "../i18n/index.tsx";
import { IconClose } from "./Icons.tsx";

type Props = {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** "modal" is centred; "drawer" slides in from the right (full screen on phones). */
  kind?: "modal" | "drawer";
  size?: "sm" | "md" | "lg";
  /** Block closing (e.g. while a request is running). */
  busy?: boolean;
};

/**
 * Native <dialog> opened with showModal(): the rest of the page is inert (focus stays inside),
 * Escape closes it, focus returns to the element that opened it.
 */
export function Dialog({ open, onClose, title, description, children, footer, kind = "modal", size = "md", busy = false }: Props) {
  const t = useT();
  const ref = useRef<HTMLDialogElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const descId = useId();

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      opener.current = document.activeElement as HTMLElement | null;
      d.showModal();
      document.documentElement.classList.add("dialog-open");
      const first =
        d.querySelector<HTMLElement>("[data-autofocus]") ??
        d.querySelector<HTMLElement>(".dlg-body input:not([type=hidden]), .dlg-body select, .dlg-body textarea") ??
        d.querySelector<HTMLElement>(".dlg-close");
      first?.focus();
    } else if (!open && d.open) {
      d.close();
    }
  }, [open]);

  useEffect(() => {
    if (open) return;
    if (!document.querySelector("dialog[open]")) document.documentElement.classList.remove("dialog-open");
    const o = opener.current;
    if (o && document.contains(o)) o.focus();
    opener.current = null;
  }, [open]);

  useEffect(
    () => () => {
      if (!document.querySelector("dialog[open]")) document.documentElement.classList.remove("dialog-open");
    },
    [],
  );

  return (
    <dialog
      ref={ref}
      className={`dlg dlg-${kind} dlg-${size}`}
      aria-labelledby={titleId}
      aria-describedby={description ? descId : undefined}
      onCancel={(e) => {
        e.preventDefault();
        if (!busy) onClose();
      }}
      onMouseDown={(e) => {
        // A press on the backdrop (the dialog element itself, outside the panel) closes it.
        if (e.target === ref.current && !busy) onClose();
      }}
    >
      {open && (
        <div className="dlg-panel">
          <header className="dlg-head">
            <div>
              <h2 id={titleId}>{title}</h2>
              {description && <p id={descId} className="dlg-desc">{description}</p>}
            </div>
            <button type="button" className="icon-btn dlg-close" onClick={onClose} disabled={busy} aria-label={t.common.close}>
              <IconClose />
            </button>
          </header>
          <div className="dlg-body">{children}</div>
          {footer && <footer className="dlg-foot">{footer}</footer>}
        </div>
      )}
    </dialog>
  );
}
